<template>
  <div class="account-page payments_form" v-if="isLoggedIn">
    <v-layout class="page-layout sl add-funds">
      <v-flex class="payment-container">
        <v-form ref="form"
                id="form"
                v-model="valid"
                lazy-validation
                @submit.prevent="submit">
          <h1 v-if="!showSightlineImg">{{ $t('Transaction.Sightline.Deposit.Title') }}</h1>
          <img v-if="showSightlineImg" class="PlayPlusImg" :src="mediaServer('/static/brand-img/' + brandKey + '/sightline.png')" alt="Sightline" />
          <p>{{ $t('Transaction.Sightline.Deposit.Description') }}</p>
          <div v-if="!isProcessingInstructionFetch && isInstructionVerified">
            <v-text-field inputmode="decimal"
                          v-model="formData.amount"
                          :label="$t('Transaction.Amount')"
                          :prefix="currencySymbol"
                          required
                          :disabled="isProcessing"
                          :rules="[formRules.amount.required, formRules.amount.lessThanMin, formRules.amount.moreThanMax, formRules.amount.max2Decimals]"
                          class="my-3 input-amount">
            </v-text-field>
            <v-btn :disabled="isProcessing || !valid" color="success" @click="submit">{{ $t('Transaction.AddFunds') }}</v-btn>
            <v-btn :disabled="isProcessing" color="error" @click="reset">{{ $t('Transaction.ResetForm') }}</v-btn>
          </div>
          <div v-if="!isProcessingInstructionFetch && !isInstructionVerified">
            <p><v-icon color="blue" class="mr-1" size="16">info</v-icon>{{ $t('Transaction.Sightline.EnrollmentNotCompletedNote') }}</p>
          </div>
        </v-form>
      </v-flex>
    </v-layout>
    <div class="go-back">
      <span class="go-back-content" @click="goToHomepage()"><v-icon>navigate_before</v-icon><span>{{ $t('Header.navigationBack') }}</span></span>
    </div>

    <ProcessingDialog v-model="getProcessingDialog"
                      :isDialogPersistent="true"
                      :isProcessing="isProcessing"
                      :isSuccess="!transactionError"
                      :processingTitle="$t('Transaction.Dialog.Processing.Title')"
                      :processingText="$t('Transaction.Dialog.Processing.Text')"
                      :successTitle="$t('Transaction.Dialog.Success.Title')"
                      :successText="$t('Transaction.Dialog.Success.Text')"
                      :errorTitle="$t('Transaction.Dialog.Error.Title')">
      <div slot="text-error">
        <p v-if="brandKey !=='sb'" class="generic-icon-note"><v-icon color="error" class="mr-1">error</v-icon><span>{{ $t('Transaction.Dialog.Error.Text') }}</span></p>
        <p>{{ transactionError }}</p>
        <p v-if="brandKey ==='sb'" v-html="customerServiceMessage"></p>
        <!-- <p>{{ $t('Transaction.Dialog.Error.Text.ContactSupport') }}</p> -->
      </div>
    </ProcessingDialog>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import config from '@/config'
import Screen from '@/components/mixins/Screen'
import ProcessingDialog from '@/components/common/ProcessingDialog'
import Url from '@/components/mixins/Url'
import Branding from '@/components/mixins/Branding'
let vm = null

export default {
  name: 'IWdepositSightline',
  mixins: [
    Screen, Url, Branding
  ],

  components: {
    ProcessingDialog
  },

  created () {
    vm = this
  },

  data: () => ({
    formData: {
      amount: ''
    },
    formRules: {
      amount: {
        required (value) { return !!value || vm.$t('Transaction.Amount.IsRequired') },
        lessThanMin (value) {
          const minAmount = vm.minDepositAmount
          if (minAmount) {
            return value >= minAmount || vm.$t('Transaction.Amount.Deposit.MinAmount') + ' $' + minAmount
          } else {
            return value > 0 || vm.$t('Transaction.Amount.MustBeMoreThan') + ' $0' // by default amount must be more than $0
          }
        },
        moreThanMax (value) {
          const maxAmount = vm.maxDepositAmount
          if (maxAmount) {
            return value <= maxAmount || vm.$t('Transaction.Amount.Deposit.MaxAmount') + ' $' + maxAmount
          } else {
            return true // no max amount limit
          }
        },
        max2Decimals: v => /^\s*(?=.*[1-9])\d*(?:\.\d{1,2})?\s*$/g.test(v) || vm.$t('Transaction.Amount.Deposit.Max2Decimals')
      }
    },

    valid: true,
    transactionError: null,
    customerServiceMessage: null
  }),

  methods: {
    async submit () {
      if (this.$refs.form.validate()) {
        try {
          this.$store.commit('sightline/isTransactionProcessingInvoked', true)
          this.transactionError = null
          this.customerServiceMessage = null
          // this.$store.commit('sightline/setProcessingDialog', true)
          await this.$store.dispatch('sightline/depositSightlineFlow', {'amount': this.formData.amount})
          this.reset()
        } catch (err) {
          this.customerServiceMessage = err.exceptionType === 'AccountNotVerified' ? this.$t('Transaction.Dialog.Error.ContactSupport') : this.$t('Transaction.Dialog.Error.Text.ContactSupport')
          this.transactionError = err.message || this.$t('Server.ErrorMsg.Generic')
          this.$store.dispatch('sightline/sendAnalytics', 'failed')
        } finally {
          this.$store.commit('sightline/isTransactionProcessingInvoked', false)
        }
      }
    },

    reset () {
      this.$refs.form.reset()
    },

    goToHomepage () {
      this.$router.push({name: 'sightline'})
    },

    initialize () {
      if (this.isLoggedIn && !this.isInstructionVerified) {
        this.$store.dispatch('sightline/fetchInstruction')
      }
    }
  },

  computed: {
    ...mapGetters(['isLoggedIn']),

    ...mapState('sightline', {
      isProcessingInstructionFetch: 'isProcessingInstructionFetch',
      isProcessingDeposit: 'isProcessingDeposit',
      pollingTransactionError: 'pollingTransactionError'
    }),

    ...mapGetters('sightline', {
      isInstructionVerified: 'isInstructionVerified',
      isPollingTransaction: 'isPollingTransaction',
      minDepositAmount: 'minDepositAmount',
      maxDepositAmount: 'maxDepositAmount'
    }),

    isProcessing () {
      return this.isPollingTransaction || this.isProcessingInstructionFetch || this.isProcessingDeposit || this.isTransactionProcessingInvoked
    },
    showSightlineImg () {
      return config.app.SIGHTLINE_IMG
    },
    currencySymbol () {
      return config.app.CURRENCY
    },
    isTransactionProcessingInvoked () {
      return this.$store.getters['sightline/getIsTransactionProcessingInvoked']
    },
    getProcessingDialog () {
      return this.$store.getters['sightline/getProcessingDialog']
    }
  },

  watch: {
    isLoggedIn (newVal, oldVal) {
      if (newVal) {
        this.initialize()
      }
    },
    pollingTransactionError (newVal) {
      this.transactionError = newVal
    }
  },

  mounted () {
    this.initialize()
  }
}
</script>

<style lang="stylus" scoped>
@import "~@/css/config"
.payment-container
  text-align center
  #form
    margin 0 auto
    max-width 100%
    background-color #fff
    border 10px solid #fff
  #spanexpress
    display none
.input-amount
  >>> .v-text-field__prefix
    margin-top: 10px
    padding: 0
</style>



// WEBPACK FOOTER //
// src/components/account/IntegratedWallet/IWdepositSightline.vue